import type { FormatSupports } from "@norkron/web-elaborate-editor";

/**
 * `canvasCapabilities` — the ONE honest declaration of what the paginated Canvas
 * editing surface can and cannot do.
 *
 * WHY THIS EXISTS: the ribbon's enable gate reads the loaded `docz.wasm` export
 * table (`FormatSupports`), NOT the mounted editor handle. The shipped
 * `docz.wasm` exports every probed verb, so a command whose handle member is a
 * stub renders fully ENABLED over the canvas — a click does nothing and says
 * nothing. This file is what stops that.
 *
 * IT CUTS BOTH WAYS, and 2026-07-29 is when the other edge mattered. `tables` and
 * `blockPalette` sat here as `false` with "TODO C4 — blocked on the pdfz per-cell
 * geometry provenance FR" long after that FR had LANDED. A declaration that is
 * merely STALE is the same defect as one that overclaims: it kept the contextual
 * Table ribbon group off the default editing surface entirely (`inTable` is the
 * gate the tab's existence hangs on), which is exactly what the owner reported.
 * So a group here is only as good as the test beside it — every `available: true`
 * below is carried by a suite that drives the REAL wasm, and every
 * `available: false` names the MEASUREMENT that justifies it.
 *
 * This module is the surface's OWN capability statement: the
 * handle publishes it as `handle.capabilities`, and the toolbar env intersects it
 * with the wasm-derived `FormatSupports` so a surface can only ever SUBTRACT
 * (never fake) a capability.
 *
 * INVARIANT (guarded by `canvasCapabilities.test.ts`):
 *   · every method on the handle belongs to exactly one group below, so a NEW
 *     stub cannot be added without declaring it;
 *   · every group whose `available` is false must actually decline at runtime
 *     (`false` / `null` / `[]` — never a fabricated success);
 *   · every `capability` named here must be a real `FormatSupports` key, so the
 *     declaration reaches the ribbon's gate rather than being silently ignored.
 */

/** The surface's capability override — a SUBSET of the toolbar's `FormatSupports`. */
export type CanvasCapabilities = Partial<FormatSupports>;

export interface CanvasVerbGroup {
  /** Stable group id (also the guard-test key). */
  readonly id: string;
  /** The `FormatSupports` gate this group projects onto, or `null` when the
   *  toolbar contract has no key for it (→ the ribbon CANNOT be gated; see
   *  `ungatedGaps` below and the upstream FR in the report). */
  readonly capability: keyof FormatSupports | null;
  /** True when the canvas handle really performs these verbs. */
  readonly available: boolean;
  /** Every handle member this group covers (the guard test asserts totality). */
  readonly verbs: readonly string[];
  /** Short human phrase for the user-facing "unavailable in this view" list. */
  readonly label: string;
  /** Why it is (un)available — the honest reason, not a marketing line. */
  readonly why: string;
  /**
   * Set on a group that IS available but only over a documented SUBSET of its
   * inputs — the third state `available: boolean` cannot express.
   *
   * It exists because flattening a partial capability to `true` silently drops a
   * true statement from {@link CANVAS_UNAVAILABLE_LABEL}: the user would be told
   * block-kind conversion works, and then watch a quote conversion refuse. A
   * caveat is carried into the user-facing sentence exactly like an unavailable
   * group's label, so the view toggle stays honest at the granularity the
   * surface actually has.
   */
  readonly caveat?: string;
}

export const CANVAS_VERB_GROUPS: readonly CanvasVerbGroup[] = [
  {
    id: "save",
    capability: null,
    available: true,
    verbs: ["toHtml", "root", "structuralLossOnSave"],
    label: "save / export serialization",
    why: "Serializes the live docz IR (marks included) — the same renderer the workbench Save posts. `structuralLossOnSave` is the REFUSAL predicate the host asks before it writes: the live model's structural census vs the census of the exact bytes about to be posted, so a serializer that would destroy a table / list / quote blocks the save instead of shipping it.",
  },
  {
    id: "textEdit",
    capability: null,
    available: true,
    verbs: ["applyTextEdit"],
    label: "typing and programmatic text edits",
    why: "Real: routed through DoczEditBridge → docz.wasm, so comment/format marks split and shift.",
  },
  {
    id: "inlineFormat",
    capability: "styleMarks",
    available: true,
    verbs: ["toggleMark", "setMark", "clearFormatting", "changeCase", "activeFormat"],
    label: "inline formatting",
    why: "Real (C2): the canvas format layer drives the native docz mark verbs.",
  },
  {
    id: "paraProps",
    capability: "paraProps",
    available: true,
    verbs: ["setParaProps"],
    label: "paragraph geometry",
    why: "Real: paragraph props ride the structured encoder into pl_layout and reflow.",
  },
  {
    id: "pageDecoration",
    capability: "pageDecoration",
    available: true,
    verbs: ["setPageDecoration", "pageDecoration"],
    label: "page decoration",
    why: "Real for PAINT, NOT for SAVE (Bundle 2 + margins): page color / borders / line numbering re-frame the PLCF V2 header and page margins re-frame the text column, all repainting live — but none of it survives save+reload, because the save wire is HTML over a flat block list and carries no section/document envelope. See the setPageDecoration comment in CanvasElaborateEditor.",
  },
  {
    id: "selection",
    capability: null,
    available: true,
    verbs: ["hasSelection", "selectionViewportRect"],
    label: "selection reporting",
    why:
      "Real: answers from the wasm editor core's {anchor,focus} IR coordinate pair via the shared " +
      "upstream `canvasHasSelection`, and projects the live selection rects to viewport coordinates " +
      "through the engine's own `viewportRectFor`. This is what every `requires: 'selection'` ribbon " +
      "command (Insert > Link) gates on and where the comment composer is placed; a surface that " +
      "cannot answer leaves them dead.",
  },
  {
    id: "focusHandback",
    capability: null,
    available: true,
    verbs: ["focus"],
    label: "focus handback",
    why:
      "Real: the toolbar's `after()` calls `editorHandle.focus?.()` when focus has landed inside " +
      "`.ela-toolbar` (keyboard activation, popovers) — the paths with no `mousedown` to cancel. The " +
      "canvas delegates to the SURFACE, which knows that 'focused' means its offscreen HiddenInputSink " +
      "textarea, not its container. Unimplemented, the optional call resolves to `undefined` forever and " +
      "Cmd+Z after a ribbon command reaches the button instead of the document — measured live 2026-07-27.",
  },
  {
    id: "commentAnchors",
    capability: null,
    available: true,
    verbs: [
      "buildSelector",
      "anchorRefusal",
      "placeCommentMark",
      "resolveAnchor",
      "highlightAnchor",
    ],
    label: "comment anchors",
    why:
      "Real (C3): offset-native selectors, a real applyCommentMark verb, and painted highlight bands. " +
      "CROSS-BLOCK since 2026-07-29 — a selection spanning paragraphs, list items, quote children and " +
      "table cells is named by ONE selector (per-block sub-ranges) and carries the Mark in EVERY block " +
      "of the range. `anchorRefusal` is the honesty half: only a surface knows which BLOCK KINDS its " +
      "selection covers, and a range touching a table / list / code island is stored as markup rather " +
      "than text in the section body, so its anchor cannot be re-found. The composer opens either way " +
      "and DISCLOSES the limit — it never refuses to open.",
  },
  {
    id: "history",
    capability: null,
    available: true,
    verbs: ["undo", "redo", "canUndo", "canRedo", "historyBarrier"],
    label: "undo / redo",
    why: "Real: the undo unit is a docz IR snapshot in EditorCore's bounded ring (web-docz's shared SnapshotHistory) — never a TS shadow document.",
  },
  {
    id: "clipboard",
    capability: null,
    available: true,
    verbs: ["cut", "copy", "paste", "pasteFromSystem", "canPaste"],
    label: "cut / copy / paste",
    why: "Real: routed through canvasEditorServices onto the web-docz clipboard codec (the same three flavours the DOM editor reads/writes).",
  },
  {
    id: "formatPainter",
    capability: null,
    available: true,
    verbs: ["formatPainter", "painterArmed"],
    label: "format painter",
    why: "Real: the shipped painter primitive captures from and applies to the canvas format layer.",
  },
  {
    id: "keyboard",
    capability: null,
    available: true,
    verbs: ["handleShortcut"],
    label: "keyboard shortcuts",
    why: "Real: dispatches through the ONE command catalog the ribbon and the DOM editor share, AFTER the input sink's caret-navigation mapping.",
  },
  {
    id: "blockKind",
    capability: null,
    // PARTIAL. web-page-layout 09341e8 (blitz-9) promoted the HEADING namespace and
    // b50545a promoted LIST ITEMS and single-paragraph QUOTES, so those conversions
    // now perform real, caret-addressable work. `code` still refuses.
    available: true,
    verbs: ["setBlockKind"],
    label: "block-kind conversion",
    why:
      "Real for paragraph <-> heading / list item / quote: web-page-layout pairs the AUX ordinal " +
      "namespace per RUN KIND (heading, list item) and a single-child quote takes an ordinary " +
      "paragraph ord, so each converted block carries per-line (block_ord, byte) provenance and a " +
      "caret can enter it. The verb gates on the SAME upstream `isCanvasAddressableKind` predicate, " +
      "evaluated against the block BEING CONVERTED, so this declaration cannot drift from the engine.",
    caveat:
      "converting a block to a code block (pdfz stamps code lines but reports them as ordinary " +
      "paragraph runs, so nothing on the wire names them and no ordinal namespace can pair them — " +
      "the verb refuses rather than painting a caret-less dead zone), converting a MULTI-LINE block " +
      "to a quote (a quote is addressable only while it maps 1:1 onto one paragraph ordinal), and " +
      "TYPING INSIDE a quote or a list item once the document has been formatted (web-docz's " +
      "structured transport has no sub-block addressing yet, so the keystroke is an honest no-op — " +
      "convert the block back to a paragraph to edit it)",
  },
  {
    id: "blockPalette",
    capability: "blockPalette",
    available: true,
    verbs: ["insertBlock"],
    label: "structural inserts (tables, horizontal rules)",
    why:
      "Real: `canvasInsertLayer` places the STRUCTURED block after the caret's own " +
      "top-level block through web-page-layout's `canvasInsertStructuredAfter` (which " +
      "rebuilds the docz handle in `docz.wasm`), installs it via `EditorCore.applyBridge` " +
      "as ONE undoable step, and puts the caret in the first addressable region of what " +
      "was inserted. MEASURED end to end: an inserted 3x3 table projects nine " +
      "caret-addressable `table_cell` blocks with correct sub-block addresses, the first " +
      "keystroke lands in the right cell, `toHtml()` carries it, and the structural " +
      "census of the posted bytes shows ZERO loss — so it cannot trip the save refusal.",
    caveat:
      "inserting an IMAGE (the layout engine cannot read a picture on this surface — " +
      "`srcKind:'path'` makes `pl_layout` fail the WHOLE document rather than degrade, " +
      "and `srcKind:'bytes'` cannot carry a payload through the DTO's UTF-8 string " +
      "field; the verb refuses OUT LOUD through the same toast channel a refused click " +
      "uses, and `insert.image` is greyed out via the `images` gate below)",
  },
  {
    id: "images",
    capability: "images",
    available: false,
    // No handle verb of its own: an image arrives through `insertBlock`, which
    // REFUSES it (see the `blockPalette` caveat). The group exists because the
    // toolbar needs the CAPABILITY subtracted so `insert.image` renders disabled
    // with a reason instead of enabled-and-refusing. Declaring a gate the surface
    // cannot honour, with no verb attached, is the honest shape here.
    verbs: [],
    label: "image insertion",
    why:
      "MEASURED, not assumed. `FormatApi.insertImage()` emits `{srcKind:'path'}`, and " +
      "`pl_layout` does not degrade on a path a browser cannot resolve — it returns " +
      "`LAYOUT` and the ENTIRE document fails to lay out, so one image insert would " +
      "blank the canvas. The `srcKind:'bytes'` alternative cannot carry a payload from " +
      "TypeScript either: `block-dto.ts` writes `w.str(block.src)` as UTF-8, so a byte " +
      "string is mangled on the wire (`0x89` arrives as `0xc2 0x89`; the rendered " +
      "`<img src>` begins `wolQTkcN` where the source was `iVBORw0`). FR filed upstream.",
  },
  {
    id: "pageBreak",
    capability: "pageBreak",
    available: false,
    verbs: ["insertPageBreak"],
    label: "page breaks",
    why:
      "MEASURED, not assumed. A structured `{kind:'pageBreak'}` block laid out through " +
      "the real `pl_layout` leaves the page count UNCHANGED whether it sits mid-document " +
      "or at the end, and `handle.toHtml()` renders it as the EMPTY STRING — so it would " +
      "neither break a page nor survive save + reload. Both halves are missing upstream " +
      "(the pdfz layout pass and docz's `html_writer`); FRs filed. Until they land the " +
      "verb refuses out loud and the command renders DISABLED with the reason, which is " +
      "strictly better than the state it replaced: `insert.page-break` carries no " +
      "`requires` gate of its own upstream, so it used to render ENABLED and silently " +
      "dead on this surface.",
  },
  {
    id: "insertMenu",
    capability: null,
    available: false,
    verbs: ["getInsertMenu"],
    label: "the content-model insert menu",
    why:
      "BY DESIGN, both surfaces: `getInsertMenu` is an upstream SCAFFOLD pending a docz " +
      "content-model WASM export (`web-elaborate-editor` `types.ts` says so), and no " +
      "shipped ribbon command calls it. Returning `[]` is the same answer the DOM editor " +
      "gives. Declared separately from the working inserts so turning the block palette " +
      "on does not quietly claim this too.",
  },
  {
    id: "lists",
    capability: "lists",
    available: true,
    verbs: [
      "setListStyle",
      "setListItemLevel",
      "restartList",
      "toggleTaskList",
      "setBulletGlyph",
    ],
    label: "lists and numbering",
    why:
      "Real. The blocker named here until 2026-07-31 — 'list blocks are not " +
      "canvas-addressable yet (TODO C2/C4)' — was NEVER TRUE: web-docz's " +
      "`legacyFromStructuredBlock` has projected one `list_item` block per ITEM with " +
      "its own sub-block address (`L::li:0`) since the address landed, " +
      "web-page-layout's `block-map.ts` promotes `list_item` into the AUX `listItem` " +
      "ordinal namespace, and `isCanvasAddressableKind` has answered true for it all " +
      "along — a caret could sit in a list item and bold it. The REAL blocker was one " +
      "layer down: the four AUTHORED list properties (`ListItem.checked`, " +
      "`List.start_override`, `ListOptions.bullet_char`, `ListItem.level`) died at the " +
      "docz→flex boundary inside pdfz, so the engine painted `•` whatever the model " +
      "said. pdfz `1e096a4` carries all four through, web-page-layout `f79531a` " +
      "re-vendored the blob and `web/pdfz-layout.wasm.lock` moved to it — so a list " +
      "verb can now change a pixel. The five verbs run docz.wasm's own six " +
      "`dz_transform_*` list verbs through `canvasListLayer`, folded BLOCK-scoped over " +
      "web-docz's `applyAcrossBlocks` so a THREE-PARAGRAPH selection converts three " +
      "blocks (`setBlockKind` still resolves one ordinal — that is the defect this " +
      "group does not repeat). Carried by " +
      "`canvasListRibbon.scenario.test.ts` + `canvasListChecklist.scenario.test.ts` " +
      "(model + PAINTED MARKER TEXT off the real `pdfz-layout.wasm` for all four " +
      "properties) and `canvasListTab.test.tsx` (the real ribbon buttons, clicked). " +
      "Checklist CONVERTS body text, like its two `list-type` cluster siblings; the " +
      "channel verbs (restart / bullet-glyph / demote / promote) need a list that " +
      "already exists and refuse a paragraph BY NAME, on the surface's refusal " +
      "channel rather than silently.",
    caveat:
      "a list nested INSIDE a blockquote (`Q::bq:0::li:1`) — its container is not a " +
      "top-level block, so splicing an item back would rewrite the wrong block and " +
      "the verb declines by name instead; and CROSS-BLOCK numbering continuation, " +
      "which `List.num_id` expresses in the docz IR but `pdfz/src/flex.zig` never " +
      "reads — so consecutive lists produced by one gesture are MERGED into a single " +
      "block (the only spelling of \"one numbered list\" this engine's raster can " +
      "express) rather than left sharing a `numId` that nothing honours",
  },
  {
    id: "tables",
    capability: "tables",
    available: true,
    verbs: ["table", "inTable", "tableSelection", "hasCellRange", "tableHeaderOn"],
    label: "tables",
    why:
      "Real. The blocker named here until 2026-07-29 — 'no canvas cell model (TODO C4), " +
      "blocked on the pdfz per-cell geometry provenance FR' — LANDED: pdfz emits " +
      "`cellRow`/`cellCol` (SoA v5) plus `tableOrd` (v7), web-docz projects ONE " +
      "`table_cell` block per cell carrying its sub-block address (`T::th:2`, " +
      "`T::td:3:1`), and web-page-layout routes an edit to one. So `inTable` — the ONE " +
      "gate the contextual Table TAB's very existence hangs on upstream " +
      "(`CONTEXTUAL_TABS = { table: 'inTable' }`) — is now derived from the caret's real " +
      "geometry via `canvasCellAt`, which makes it true for EVERY table in the document " +
      "including the ones that arrived from the server (the DOM controller's " +
      "`hasTableTarget()` can only see tables inserted in the current session). The 16 " +
      "verbs run the grid math in `docz.wasm` through web-docz's `applyTableVerb` over " +
      "the 12 shipped `dz_table_*` exports; nothing about a table is modelled here.",
    caveat:
      "splitting a cell into an N-by-M grid (docz's `dz_table_split_cell` only " +
      "UN-MERGES — its `intoRows`/`intoCols` arguments are measurably inert, so Split " +
      "on a cell that was never merged declines rather than pretending), deleting a " +
      "MULTI-COLUMN selection in one go (the column verb removes the region's first " +
      "column only, unlike the row verb which sweeps the whole span), row banding " +
      "RE-STRIPING itself after a row insert or delete (a band is a concrete per-cell " +
      "fill, so re-apply Banding to restripe), and a cell region on a RAGGED grid " +
      "(rows of differing cell counts — the docz wire permits it, Word does not emit " +
      "it), where the unified-visual (row, column) arithmetic reports the widest row " +
      "and a verb may address a neighbouring cell",
  },
  {
    id: "proposals",
    capability: null,
    available: false,
    verbs: ["showProposal", "acceptProposal", "rejectProposal"],
    label: "agent proposals (show / accept / reject)",
    why: "STUB: agent edits are human-approval-gated pending revisions (brief §7); not wired on canvas.",
  },
  {
    id: "validation",
    capability: null,
    available: false,
    verbs: ["validateBlock"],
    label: "block validation diagnostics",
    why: "STUB: returns no diagnostics on this surface.",
  },
  {
    id: "anchorRebase",
    capability: null,
    available: false,
    verbs: ["rebaseAnchor"],
    label: "client-side anchor rebase",
    why:
      "BY DESIGN: rebase is a COMMIT-time server/docz concern (brief §6). Returning null routes the " +
      "host to the server rather than guessing a client re-anchor.",
  },
];

/**
 * Product-level gaps that are NOT handle verbs: the workbench wiring around the
 * canvas that still assumes the DOM editor. Surfaced to the user in the view
 * toggle's title so the paginated view never looks feature-complete when it isn't.
 */
export const CANVAS_SURFACE_GAPS: readonly string[] = [
  // CLOSED 2026-07-27 — "new comments" used to sit here. `ArticleWorkbench`'s
  // `addComment` read `window.getSelection()` inside
  // `[data-testid="elaborate-editor"]`; the canvas has neither, so Review ▸ New
  // comment silently early-returned on the DEFAULT editing surface. It now goes
  // through `commentTargetFromSurface` → the upstream surface-agnostic
  // `handle.buildSelector()` (no Range = "your own live selection"), which both
  // surfaces answer. Pinned by `canvasNewComment.test.ts`.
  //
  // Anything listed here is shown to the user in the view toggle's title, so the
  // paginated view never looks feature-complete when it isn't. Empty is a claim,
  // not an oversight — add a line the moment a surface-level gap reappears.
];

/** The handle member that carries this declaration (excluded from verb totality). */
export const CANVAS_CAPABILITY_FIELD = "capabilities";

/** The capability override the canvas handle publishes: derived from the groups
 *  above so the declaration can never drift from the verbs it describes. */
export const CANVAS_CAPABILITIES: CanvasCapabilities = Object.freeze(
  Object.fromEntries(
    CANVAS_VERB_GROUPS.filter((g) => g.capability !== null).map((g) => [
      g.capability as string,
      g.available,
    ]),
  ),
) as CanvasCapabilities;

/** Every group that declines, in declaration order. */
export function unavailableGroups(): readonly CanvasVerbGroup[] {
  return CANVAS_VERB_GROUPS.filter((g) => !g.available);
}

/** Every group that is available, but only over a documented SUBSET of its
 *  inputs (see {@link CanvasVerbGroup.caveat}), in declaration order. */
export function partialGroups(): readonly CanvasVerbGroup[] {
  return CANVAS_VERB_GROUPS.filter((g) => g.available && g.caveat != null);
}

/** The user-facing "not available in this view" phrase (view-toggle title).
 *  Carries the PARTIAL groups' caveats too — a capability that works for one
 *  input and refuses another is not something to tell the user only half of. */
export const CANVAS_UNAVAILABLE_LABEL: string = [
  ...unavailableGroups().map((g) => g.label),
  ...partialGroups().map((g) => g.caveat as string),
  ...CANVAS_SURFACE_GAPS,
].join(", ");

/**
 * The declining groups the ribbon CANNOT grey out, because the toolbar's
 * `FormatSupports` contract has no gate for them (`capability === null`).
 *
 * These are the residue of the dead-button fix: `blockPalette` / `lists` /
 * `tables` reach the enable gate and render DISABLED with a reason, but
 * block-kind conversion, agent proposals, block validation and client-side
 * anchor rebase have no key to project onto — so their controls still render
 * live on the canvas and merely FAIL HONESTLY (the handle returns `null`) when
 * clicked. The only user-facing channel left for them is
 * {@link CANVAS_UNAVAILABLE_LABEL} on the view toggle, which is why the guard
 * test asserts every one of them is named there.
 *
 * Closing this properly needs an upstream `FormatSupports` key in
 * `@norkron/web-elaborate-editor` (`commands/registry.ts` +
 * `document-format.ts` + the `requires` on the style/heading commands). Once it
 * exists, the fix here is ONE edit: set this group's `capability` to the new key
 * and it starts greying out — no other change.
 */
export function ungatedGaps(): readonly CanvasVerbGroup[] {
  return CANVAS_VERB_GROUPS.filter((g) => !g.available && g.capability === null);
}
